// ─────────────────────────────────────────────────────────────────────────────
// branding.js  —  /promo/:branding  (e.g. /promo/cup, /promo/ad-cup, /promo/facebook, /promo/instagram …)
//
// Namespaced under /promo/ so this dynamic segment can never collide with a
// real app route (/listings, /about, etc.) — safe to add new campaign slugs
// anytime without touching routing code.
//
// Route it in App.js:
//   <Route path="/promo/:branding" element={<Branding />} />
//
// Everything here is static for now. The listing rows use dummy data —
// swap STAY_GROUPS with your API response later.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import {
  QrCode, ShieldCheck, Sparkles, Heart,
  Search, MessagesSquare, KeyRound,
  BadgePercent, MessageCircle, IndianRupee, ArrowRight,
} from "lucide-react";
// import { useUI } from "../context/uicontext";
// import SEO from "./SEO";

// ─── 1. Quirky copy per branding surface ─────────────────────────────────────
// 3 variations each — one is picked at random on every visit.
const BRANDING_COPY = {
  "ad-cup": [
    { badge: "You scanned our chai cup ☕", line1: "Drinking tea?", line2: "Why not book a PG :)" },
    { badge: "Straight off the cup ☕", line1: "Chai done. Kya plan?", line2: "Let's find your room :)" },
    { badge: "Cup pe milte hain ☕", line1: "Ek cutting, ek kamra?", line2: "Both sorted here :)" },
  ],
  facebook: [
    { badge: "You came from Facebook 👋", line1: "Scrolling the feed?", line2: "Your next PG is right here :)" },
    { badge: "Hello, Facebook friend 👋", line1: "Done liking posts?", line2: "Now like a room :)" },
    { badge: "Straight from the timeline 👋", line1: "Feed refreshed?", line2: "Time to refresh your stay :)" },
  ],
  instagram: [
    { badge: "You came from Instagram 📸", line1: "Done scrolling reels?", line2: "Let's find your room :)" },
    { badge: "Swiped in from the gram 📸", line1: "Saved 40 reels today?", line2: "Save a PG instead :)" },
    { badge: "Straight from our story 📸", line1: "Story dekh li?", line2: "Ab ghar bhi dekh lo :)" },
  ],
};

// Fallback for any unknown surface (water bottles, posters, campaigns…)
const DEFAULT_COPY = [
  { badge: "You scanned an AapkaPG code ✨", line1: "Scanned our code?", line2: "Let's find you a PG :)" },
  { badge: "Welcome to AapkaPG ✨", line1: "Nice scan!", line2: "Now pick your room :)" },
  { badge: "You found us ✨", line1: "Looking for a stay?", line2: "You're in the right place :)" },
];

// Extra safety net (not load-bearing now that this page lives under /promo/):
// if someone hits /promo/<reserved-word> directly, send them home instead of
// showing a nonsense "campaign" page.
const RESERVED = ["branding", "home", "listings", "about", "contact", "blog", "pg"];

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ─── 2. Dummy listing data (replace with API later) ──────────────────────────
const DUMMY_IMG = [
  "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=700&q=70",
  "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=700&q=70",
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=700&q=70",
  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=700&q=70",
  "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=700&q=70",
  "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=700&q=70",
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=700&q=70",
];

const makeStays = (area, seed) => {
  const kinds = ["Boys PG", "Girls PG", "Co-ed PG", "Single Room", "Double Sharing", "Studio Room", "Triple Sharing"];
  return Array.from({ length: 7 }, (_, i) => {
    const n = seed * 7 + i;
    return {
      id: `${area}-${i}`,
      title: `${kinds[i % kinds.length]} in ${area}`,
      price: 7500 + ((n * 850) % 9000),
      rating: (4.5 + ((n * 7) % 50) / 100).toFixed(2),
      tag: n % 3 === 0 ? "Guest favourite" : null,
      image: DUMMY_IMG[n % DUMMY_IMG.length],
    };
  });
};

const STAY_GROUPS = [
  { key: "kamla-nagar", title: "Available in Kamla Nagar", area: "Kamla Nagar", stays: makeStays("Kamla Nagar", 1) },
  { key: "mukherjee-nagar", title: "Available in Mukherjee Nagar", area: "Mukherjee Nagar", stays: makeStays("Mukherjee Nagar", 2) },
  { key: "rajinder-nagar", title: "Available in Rajinder Nagar", area: "Rajinder Nagar", stays: makeStays("Rajinder Nagar", 3) },
];

// ─── 3. Hero (new — this is the only fully new block) ────────────────────────
function BrandingHero({ copy }) {
  return (
    <section className="brand-hero">
      <span className="brand-hero__blob brand-hero__blob--tr" />
      <span className="brand-hero__blob brand-hero__blob--bl" />

      <div className="brand-hero__inner">
        <div className="brand-hero__badge">
          <QrCode className="brand-hero__badge-icon" />
          {copy.badge}
        </div>

        <h1 className="brand-hero__title">
          {copy.line1}
          <br />
          <span className="brand-hero__title-accent">{copy.line2}</span>
        </h1>

        <p className="brand-hero__desc">
          Find Verified PGs Near You Without the Hassle. Affordable, safe, and 100% no-broker
          stays. Browse real photos, connect with owners, and book in minutes.
        </p>

        <div className="brand-hero__stats">
          <span className="brand-hero__stat">
            <ShieldCheck className="brand-hero__stat-icon" />
            12,500+ Verified PGs
          </span>
          <span className="brand-hero__stat">
            <Sparkles className="brand-hero__stat-icon" />
            Zero brokerage
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── 4. Airbnb-style listing row (dummy data for now) ────────────────────────
function StayRow({ group }) {
  const rowRef = useRef(null);
  const navigate = useNavigate();

  const scrollBy = (dir) =>
    rowRef.current?.scrollBy({
      left: dir * rowRef.current.clientWidth * 0.8,
      behavior: "smooth",
    });

  return (
    <section className="brand-stays">
      <div className="brand-stays__header">
        <button
          type="button"
          className="brand-stays__title"
          onClick={() => navigate("/listings")}
        >
          <span>{group.title}</span>
          <span className="brand-stays__title-arrow"><ArrowRight /></span>
        </button>

        <div className="brand-stays__nav">
          <button type="button" aria-label="Scroll left" onClick={() => scrollBy(-1)}>‹</button>
          <button type="button" aria-label="Scroll right" onClick={() => scrollBy(1)}>›</button>
        </div>
      </div>

      <div className="brand-stays__row" ref={rowRef}>
        {group.stays.map((stay) => (
          <article
            key={stay.id}
            className="brand-stay-card"
            onClick={() => navigate(`/pg/${stay.id}`)}
          >
            <div className="brand-stay-card__media">
              <img src={stay.image} alt={`${stay.title} in ${group.area}`} loading="lazy" />
              {stay.tag && <span className="brand-stay-card__tag">{stay.tag}</span>}
              <button
                type="button"
                className="brand-stay-card__save"
                aria-label={`Save ${stay.title}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Heart />
              </button>
            </div>
            <h3 className="brand-stay-card__title">{stay.title}</h3>
            <p className="brand-stay-card__meta">
              ₹{stay.price.toLocaleString("en-IN")}/month · ★ {stay.rating}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

// ─── 5. Blocks copied from home.js (use YOUR existing ones + their scss) ─────
const WHY_ITEMS = [
  { Icon: ShieldCheck,   title: "Verified Listings",     desc: "Every PG personally verified by our team for safety & accuracy." },
  { Icon: BadgePercent,  title: "Zero Brokerage",        desc: "No middlemen, no hidden fees. What you see is what you pay." },
  { Icon: MessageCircle, title: "Instant Owner Connect", desc: "Reach out to verified owners directly — schedule visits in seconds." },
  { Icon: IndianRupee,   title: "Best Price Guarantee",  desc: "Find a lower price elsewhere? We'll match it, every time." },
];

const HOW_STEPS = [
  { n: 1, Icon: Search,         title: "Search PG",             desc: "Filter by location, budget, gender & amenities." },
  { n: 2, Icon: MessagesSquare, title: "Chat / Schedule Visit", desc: "Connect with owners and schedule a visit instantly." },
  { n: 3, Icon: KeyRound,       title: "Book Instantly",        desc: "Pay token amount online and move in." },
];

function WhyChoose() {
  return (
    <section className="apg-section">
      <div className="apg-container">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">Why AapkaPG</div>
          <h2 className="apg-section-title">A renting experience you'll actually enjoy</h2>
          <p className="apg-section-sub apg-section-sub--center">Designed around real renters — not brokers.</p>
        </div>
        <div className="apg-why-grid">
          {WHY_ITEMS.map((it) => (
            <div key={it.title} className="apg-why-card">
              <div className="apg-why-card__icon"><it.Icon className="apg-why-icon" /></div>
              <h3 className="apg-why-card__title">{it.title}</h3>
              <p className="apg-why-card__desc">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="apg-section apg-section--cream">
      <div className="apg-container">
        <div className="apg-center-block">
          <div className="apg-eyebrow--plain apg-eyebrow--center">How it works</div>
          <h2 className="apg-section-title">Get a PG in 3 simple steps</h2>
        </div>
        <div className="apg-steps">
          <div className="apg-steps__line" />
          {HOW_STEPS.map((s) => (
            <div key={s.n} className="apg-step">
              <div className="apg-step__circle">
                <s.Icon className="apg-step-icon" />
                <span className="apg-step__num">{s.n}</span>
              </div>
              <h3 className="apg-step__title">{s.title}</h3>
              <p className="apg-step__desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OwnerBanner() {
  // const { setShowAuth } = useUI();
  return (
    <section id="owners" className="apg-section">
      <div className="apg-container">
        <div className="apg-owner-banner">
          <div className="apg-owner-banner__deco" />
          <div className="apg-owner-banner__glow" />
          <div className="apg-owner-banner__grid">
            <div>
              <div className="apg-badge apg-badge--glass">
                <Sparkles className="apg-badge-icon" /> For PG Owners
              </div>
              <h2 className="apg-owner-banner__title">
                Are You a <span className="apg-text-primary">PG Owner?</span>
              </h2>
              <p className="apg-owner-banner__desc">
                List your property in 5 minutes and reach 50,000+ verified tenants searching every
                month. Free to start, no commission.
              </p>
              <div className="apg-owner-banner__perks">
                <span>✓ Free listing</span><span>✓ Verified tenants only</span><span>✓ Dashboard insights</span>
              </div>
            </div>
            <div className="apg-owner-banner__cta-wrap">
              <button
                className="apg-btn apg-btn--primary apg-btn--lg apg-btn--rx apg-btn--glow"
                // onClick={() => setShowAuth(true)}
              >
                List Your PG <ArrowRight className="apg-btn-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 6. Page ─────────────────────────────────────────────────────────────────
export default function Branding() {
  const { branding = "" } = useParams();
  const key = branding.toLowerCase();

  // Pick copy once per mount (random variation each visit).
  const copy = useMemo(
    () => pickRandom(BRANDING_COPY[key] || DEFAULT_COPY),
    [key]
  );

  // Someone hit /branding (or another reserved word) directly → send home.
  if (RESERVED.includes(key)) return <Navigate to="/" replace />;

  return (
    <main className="brand-page page apg-root">
      {/* <SEO
        title="Find Verified PGs Near You | Zero Brokerage | AapkaPG"
        description="Scanned our code? Browse verified PGs with real photos, direct owner connect and zero brokerage."
        canonicalUrl={`https://aapkapg.com/promo/${key}`}
      /> */}

      <BrandingHero copy={copy} />
      <WhyChoose />
      <HowItWorks />

      <div className="brand-stays-wrap">
        {STAY_GROUPS.map((g) => (
          <StayRow key={g.key} group={g} />
        ))}
      </div>

      <OwnerBanner />
    </main>
  );
}